import { forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils.js';
import {
  DIALOG_BACKDROP,
  DialogClosePrimitive,
  DialogContentPrimitive,
  type DialogContentPrimitiveProps,
  DialogDescriptionPrimitive,
  DialogPortalPrimitive,
  DialogRoot,
  DialogTitlePrimitive,
  DialogTriggerPrimitive,
  useDialogOpen,
} from './dialog-primitive.js';

/**
 * A modal on the native `<dialog>`, opened with `showModal()`. The browser does the parts that
 * are easy to get wrong: trapping focus, making the page behind inert, rendering above every
 * z-index. `dialog-primitive.tsx` keeps the open state in React's hands — Escape and a press on
 * the backdrop ask `onOpenChange(false)` rather than closing anything — and returns focus to
 * whatever had it before the dialog opened.
 *
 * On a phone it slides up from the bottom and can grow to the full height; on a laptop it is a
 * centred panel. Same component, because a shop uses both in the same day.
 */

export const Dialog = DialogRoot;
export const DialogTrigger = DialogTriggerPrimitive;
export const DialogClose = DialogClosePrimitive;
export const DialogPortal = DialogPortalPrimitive;

/**
 * The dim layer behind the dialog is the `<dialog>`'s own `::backdrop` (see `DialogContent`), so
 * nothing renders this any more. It is kept for callers that compose a dialog by hand: a plain
 * full-screen layer, shown while the dialog is open. It sits under the top layer the dialog
 * opens in, so it never covers the dialog.
 */
export const DialogOverlay = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { forceMount?: true }
>(function DialogOverlay({ className, forceMount, ...props }, ref) {
  const open = useDialogOpen();
  if ((!open && !forceMount) || typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={ref}
      aria-hidden="true"
      data-state={open ? 'open' : 'closed'}
      className={cn(
        'fixed inset-0 z-50 bg-foreground/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />,
    document.body,
  );
});

export const DialogContent = forwardRef<HTMLDialogElement, DialogContentPrimitiveProps>(
  function DialogContent({ className, children, ...props }, ref) {
    const { t } = useTranslation();
    return (
      <DialogContentPrimitive
        ref={ref}
        className={cn(
          // `top-auto`, `m-0` and `max-w-none` undo the browser's centring of a modal <dialog>;
          // the classes after them do the placing.
          'fixed inset-x-0 bottom-0 top-auto z-50 m-0 flex max-h-[92dvh] w-full max-w-none flex-col gap-4 overflow-y-auto rounded-t-xl border border-border bg-background p-4 text-foreground shadow-lg',
          'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:p-6',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          DIALOG_BACKDROP,
          'data-[state=open]:backdrop:animate-in data-[state=closed]:backdrop:animate-out data-[state=closed]:backdrop:fade-out-0 data-[state=open]:backdrop:fade-in-0',
          className,
        )}
        {...props}
      >
        {children}
        <DialogClose className="absolute right-3 top-3 rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <X aria-hidden="true" className="size-4" />
          <span className="sr-only">{t('common.close')}</span>
        </DialogClose>
      </DialogContentPrimitive>
    );
  },
);

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5 pr-8', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogTitlePrimitive>,
  React.ComponentPropsWithoutRef<typeof DialogTitlePrimitive>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogTitlePrimitive
      ref={ref}
      className={cn('text-lg font-semibold leading-tight', className)}
      {...props}
    />
  );
});

export const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogDescriptionPrimitive>,
  React.ComponentPropsWithoutRef<typeof DialogDescriptionPrimitive>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogDescriptionPrimitive
      ref={ref}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
});
